import { createSlice } from '@reduxjs/toolkit';
import { Contract, Opportunity, Quote, Loan } from './salesforceTypes';
import { RootState } from 'app/store';

interface SalesforceState {
  salesforceId: string;
  isSalesforceAuth: boolean;
  opportunities: Opportunity[];
  contract: Contract;
  quotes: Quote[];
  quote: Quote;
  loan: Loan;
  loans: { Id: string; Name: string }[];
}

const initialState: SalesforceState = {
  salesforceId: '',
  isSalesforceAuth: false,
  opportunities: [],
  contract: {
    Id: null,
    CompanySignedBy: null,
    ContractNumber: '',
    OwnerName: null,
    ContractTerm: null,
    CustomerSignedBy: null,
    CustomerSignedTitle: null,
    Description: null,
    EndDate: null,
    OwnerExpirationNotice: null,
    SpecialTerms: null,
    StartDate: null,
    AccountName: null,
    AccountNumber: null,
    AccountAnnualRevenue: null,
    AccountBillingStreet: null,
    AccountBillingCity: null,
    AccountBillingCountry: null,
    AccountBillingState: null,
    AccountBillingPostalCode: null,
    AccountIndustry: null,
    AccountPhone: null,
    CurrentDate: null,
  },
  quotes: [],
  quote: {
    Name: '',
    Id: '',
    Pricebook2: {
      attributes: {
        type: '',
        ulr: '',
      },
      Id: '',
      Name: '',
    },
    BillingStreet: null,
    BillingName: null,
    BillingPostalCode: null,
    BillingState: null,
    BillingCountry: null,
    BillingCity: null,
    ShippingStreet: null,
    ShippingName: null,
    ShippingPostalCode: null,
    ShippingCity: null,
    ShippingCountry: null,
    ShippingState: null,
    ContactName: null,
    Phone: null,
    Email: null,
    Fax: null,
    Tax: null,
    ShippingHandling: null,
    QuoteLineItems: [],
    CurrentDate: null,
  },
  loan: {
    Id: null,
    Name: null,
    LoanPackageId: null,
    LoanPackageName: null,
    NumberOfDebtorsBorrowers: null,
    AnnualInterestRate: null,
    TermInMonth: null,
    PaymentFrequency: null,
    AmountLimit: null,
    AmountToBePosted: null,
    ApprovalStatus: null,
    BaseLoanAmount: null,
    Classification: null,
    Currency: null,
    DefaultRate: null,
    LoanAmountFormula: null,
    TotalFeeAmount: null,
    ScheduledPayment: null,
    NumberOfPayments: null,
    PaymentStartDate: null,
    PaymentDayOfMonth: null,
    MaturityDate: null,
    NewBS2Code: null,
    PartyName: null,
    PartyRole: null,
    OrderNumber: null,
    AccountId: null,
    AccountName: null,
    AccountCity: null,
    AccountCountry: null,
    AccountState: null,
    AccountStreet: null,
    CurrentDate: null,
    LoanPurpose: null,
    PaymentFrequencyUnit: null,
    BaseLoanAmountInEnglish: null,
    LoanAmountFormulaInEnglish: null,
    ScheduledPaymentInEnglish: null,
    PrimaryBorrowerName: null,
    PrimaryBorrowerRole: null,
    GuarantorName: null,
    GuarantorSalaryAssingment: null,
    GuarantorSalaryAssingmentInEnglish: null,
    GuarantorOrderNumber: null,
    GuarantorCity: null,
    GuarantorCountry: null,
    GuarantorState: null,
    GuarantorStreet: null,
    CurrentDateDMY: null,
    BorrowersNames: null,
    PartyInvoledInTransactionNames: null,
    CollateralDescriptionHtml: null,
    HasCreditLifeInsuranceSentence: null,
    CommitmentFeeAmount: null,
    CommitmentFeePartyAccount: null,
    CommitmentFeeDescription: null,
    VisaeFeeAmount: null,
    VisaeFeePartyAccount: null,
    VisaeFeeDescription: null,
    CreditLifeFeePartyAccount: null,
    CreditLifeFeeFixedAmount: null,
    CreditLifeFeeDescription: null,
    CreditLifeFeePolicyNO: null,
    CreditLifeFeeIssuedDate: null,
    CreditLifeFeeInsuranceCompany: null,
    CollateralHypothecaryOwner: null,
    CollateralHypothecaryDescription: null,
    CollateralHypothecaryParcelNO: null,
    CollateralHypothecaryBlockNO: null,
    SecurityPledged: null,
    CollateralStatementHtml: null,
    UserSignature: null,

    //Collateral
    CollateralName: null,
    CollateralStampedToCover: null,
    CollateralDescription: null,
    CollateralCode: null,
    CollateralCodeLabel: null,
    CollateralPrimaryOwner: null,
    LifeInsuranceAssignment: null,
    ParcelNO: null,
    BlockNO: null,
    PropertyOwner: null,
    Attourney: null,
    CollateralTypeOfSalary: null,
    CollateralSalaryDeduction: null,
    CollateralFinancialAccountNumber: null,
    SalaryAssingmentCreditAccountNumber: null,
    CreditLifePolicyNO: null,
    CreditLifeIssueDate: null,
    IsFullSalary: null,
    NoIsFullSalary: null,
    InsuranceCompany: null,
    CreditLifePremiumAmount: null,
    SalaryDeductionAmountNumber: null,
    SalaryDeductionAmountText: null,
    Blank: null,
    //PIT
    Address: null,
    //LoanP
    Liabilities: null,
    CollateralStatementHtmlAllLoans: null,
    TermsAndConditions: null,
  },
  loans: [],
};

const salesforceSlice = createSlice({
  name: 'salesforce',
  initialState,
  reducers: {
    setOpportunities(state, action) {
      state.opportunities = action.payload;
    },
    setQuotes(state, action) {
      state.quotes = action.payload;
    },
    setIsSalesforceAuth(state, action) {
      state.isSalesforceAuth = action.payload;
    },
    setQuote(state, action) {
      state.quote = action.payload || initialState.quote;
    },
    setContract(state, action) {
      state.contract = action.payload || initialState.contract;
    },
    setSalesforceId(state, action) {
      state.salesforceId = action.payload;
    },
    setLoan(state, action) {
      state.loan = action.payload;
    },
    setLoans(state, action) {
      state.loans = action.payload;
    },
    resetQuote(state) {
      state.quote = initialState.quote;
    },
    resetContract(state) {
      state.contract = initialState.contract;
    },
  },
  extraReducers: {},
});

export const {
  resetQuote,
  resetContract,
  setOpportunities,
  setQuotes,
  setIsSalesforceAuth,
  setQuote,
  setLoan,
  setLoans,
  setContract,
  setSalesforceId,
} = salesforceSlice.actions;

export default salesforceSlice.reducer;

export const selectOpportunities = (state: RootState) => state.salesforce.opportunities;
export const selectQuotes = (state: RootState) => state.salesforce.quotes;
export const selectIsSalesforceAuth = (state: RootState) => state.salesforce.isSalesforceAuth;
export const selectQuote = (state: RootState) => state.salesforce.quote;
export const selectContract = (state: RootState) => state.salesforce.contract;
export const selectLoan = (state: RootState) => state.salesforce.loan;
export const selectLoans = (state: RootState) => state.salesforce.loans;
export const selectSalesforceId = (state: RootState) => state.salesforce.salesforceId;
